import { useState, useEffect } from "react";
import { UserPlus, Search, CreditCard, Upload, Zap, Clock } from "lucide-react";
import StandardSection from "./ui/StandardSection";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/utils/authContext";

// Define the static configuration (icons and colors) for steps
const stepsConfig = [
	{
		icon: UserPlus,
		color: "bg-primary-600",
		panelKey: "speedAndEfficiencyCard1Panel",
		titleKey: "speed_and_efficiency_card1_title",
		descKey: "speed_and_efficiency_card1_description",
	},
	{
		icon: Search,
		color: "bg-forest-custom",
		panelKey: "speedAndEfficiencyCard2Panel",
		titleKey: "speed_and_efficiency_card2_title",
		descKey: "speed_and_efficiency_card2_description",
	},
	{
		icon: CreditCard,
		color: "bg-primary-600",
		panelKey: "speedAndEfficiencyCard3Panel",
		titleKey: "speed_and_efficiency_card3_title",
		descKey: "speed_and_efficiency_card3_description",
	},
	{
		icon: Upload,
		color: "bg-forest-custom",
		panelKey: "speedAndEfficiencyCard4Panel",
		titleKey: "speed_and_efficiency_card4_title",
		descKey: "speed_and_efficiency_card4_description",
	},
	{
		icon: Zap,
		color: "bg-primary-600",
		panelKey: "speedAndEfficiencyCard5Panel",
		titleKey: "speed_and_efficiency_card5_title",
		// Note: Using the typo key 'speed_and_efficiency_card3_description' from your JSON for card 5
		descKey: "speed_and_efficiency_card5_description",
	},
];

// Define the type for a step's content
interface StepContent {
	icon: React.ElementType;
	color: string;
	title: string;
	description: string;
}

interface ConvenienceUSPProps {
	isExploreEnabled: boolean;
	isLoginEnabled: boolean;
	base_path: string;
}

export default function ConvenienceUSP({
	isLoginEnabled,
	base_path
}: ConvenienceUSPProps) {
	const { ref: sectionRef, isVisible } = useScrollAnimation();
	const navigate = useNavigate();
  const { isLoggedIn } = useAuth();

	// State for dynamic content, initialized as empty
	const [sectionTitle, setSectionTitle] = useState("");
	const [sectionPrimaryHeader, setSectionPrimaryHeader] = useState("");
	const [sectionDescription, setSectionDescription] = useState("");
	const [stepsContent, setStepsContent] = useState<StepContent[]>([]);

	useEffect(() => {
		const fetchContent = async () => {
			try {
				const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`;
				const response = await fetch(url);

				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				const data = await response.json();
				const value = data.find((page: any) => page.name === "Landing Page");

				if (!value) {
					throw new Error('Landing page content not found');
				}

				const speedPanel = value?.fields?.find((field: any) => field.key === "speedAndEfficiencyPanel");

				if (speedPanel) {
					// Set main section content
					setSectionTitle(speedPanel.components?.find((c: any) => c.key === "speed_and_efficiency_title")?.defaultValue || "");
					setSectionPrimaryHeader(speedPanel.components?.find((c: any) => c.key === "speed_and_efficiency_primary_header")?.defaultValue || "");
					setSectionDescription(speedPanel.components?.find((c: any) => c.key === "speed_and_efficiency_description")?.defaultValue || "");

					// Build the steps array with data from JSON
					const newSteps = stepsConfig.map(config => {
						const cardPanel = speedPanel.components?.find((c: any) => c.key === config.panelKey);
						
						return {
							icon: config.icon,
							color: config.color,
							title: cardPanel?.components?.find((c: any) => c.key === config.titleKey)?.defaultValue || "",
							description: cardPanel?.components?.find((c: any) => c.key === config.descKey)?.defaultValue || "",
						};
					});
					
					setStepsContent(newSteps);
				}
			} catch (error) {
				console.error("Error fetching content for ConvenienceUSP:", error);
				// Set content to empty on error
				setSectionTitle("");
				setSectionPrimaryHeader("");
				setSectionDescription("");
				setStepsContent([]);
			}
		};

		fetchContent();
	}, []); // Empty dependency array ensures this runs only once on mount

	const handleLaunchClick = () => {
		navigate(`${base_path}/sign-up`);
	};

	return (
		<StandardSection background="light">
			<div className="text-center mb-8 sm:mb-12 lg:mb-16">
				<div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-100 to-forest-100 border border-primary-200 text-primary-700 px-6 py-3 rounded-full text-sm shadow-sm mb-6">
					<Zap className="h-4 w-4 text-primary-600" />
					<span className="text-lg sm:text-xl leading-relaxed">{sectionTitle}</span>
					<Clock className="h-4 w-4 text-forest-custom" />
				</div>

				<h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles mb-6">
					<span className="text-gray-900 block">
						{sectionPrimaryHeader}
					</span>
				</h2>

				<p className="text-lg sm:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
					{sectionDescription}
				</p>
			</div>
			<div
				ref={sectionRef}
				className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12 px-2 sm:px-0 transition-all duration-1000 ${
					isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
				}`}
			>
				{stepsContent.map((step, index) => {
					const IconComponent = step.icon;
					return (
						<div
							key={index} // Using index as key is fine here as the list is static
							className={`text-center group transition-all duration-700 ${
								isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
							}`}
							style={{ transitionDelay: `${index * 100}ms` }}
						>
							<div className="bg-white rounded-3xl p-4 sm:p-6 lg:p-8 shadow-lg h-full transition-all duration-500 hover:shadow-2xl hover:scale-105 hover:-translate-y-2 border border-transparent hover:border-emerald-200 cursor-pointer">
								<div
									className={`${step.color} text-white rounded-full w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 flex items-center justify-center mx-auto mb-4 sm:mb-6 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110`}
								>
									<IconComponent className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 transition-transform duration-300 group-hover:scale-110" />
								</div>
								<h3 className="text-base sm:text-lg font-bold mb-2 sm:mb-3 transition-colors duration-300 group-hover:text-emerald-600">
									{step.title}
								</h3>
								<p className="text-gray-700 text-xs sm:text-sm leading-relaxed font-medium transition-colors duration-300 group-hover:text-gray-900">
									{step.description}
								</p>
							</div>
						</div>
					);
				})}
			</div>

			{isLoginEnabled && !isLoggedIn &&(
				<div className="text-center px-4 sm:px-0">
					<button
						onClick={handleLaunchClick}
						className="px-4 sm:px-6 py-2 sm:py-3 h-11 font-semibold btn-primary-gradient hover:btn-primary-gradient text-white transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-emerald-500/40 transform hover:-translate-y-1 w-full sm:w-auto inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
						data-testid="button-register-now"
					>
						Register Now
					</button>
				</div>
			)}
		</StandardSection>
	);
}